import React from 'react'
import {useTranslation} from 'react-i18next'

import {CheckboxTreeNode} from '../../common/types/tree'

type CheckboxTreeProps = {
  treeData: CheckboxTreeNode[]
  onToggleNode: (node: CheckboxTreeNode) => void
  onCheckNode: (node: CheckboxTreeNode, checked: boolean) => void
  className?: string
}

const SelectionIndicator = ({node}: {node: CheckboxTreeNode}) => {
  const {t} = useTranslation()

  if (node.isIndeterminate) {
    return <span className="text-xs text-foreground_faded">{t('roleAssignment.partial')}</span>
  }

  return null
}

const TreeNodeRow = ({
  node,
  depth,
  onToggleNode,
  onCheckNode
}: {
  node: CheckboxTreeNode
  depth: number
  onToggleNode: (node: CheckboxTreeNode) => void
  onCheckNode: (node: CheckboxTreeNode, checked: boolean) => void
}) => {
  const {t} = useTranslation()
  const hasChildren = Boolean(node.children && node.children.length > 0)

  return (
    <li
      role="treeitem"
      aria-expanded={hasChildren ? node.isOpen : undefined}
      aria-level={depth + 1}
    >
      <div
        className="flex items-center gap-2 py-1"
        style={{paddingLeft: `${depth * 16}px`}}
        data-test-id={`tree-node-${node.id}`}
      >
        {hasChildren ? (
          <button
            type="button"
            className="w-5 text-left"
            onClick={() => onToggleNode(node)}
            aria-label={node.isOpen ? t('roleAssignment.collapse') : t('roleAssignment.expand')}
          >
            {node.isOpen ? '-' : '+'}
          </button>
        ) : (
          <span className="w-5" />
        )}

        <label className={`flex items-center gap-2 text-sm ${node.isDisabled ? 'text-foreground_faded cursor-not-allowed' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={node.isSelected}
            disabled={node.isDisabled}
            aria-checked={node.isIndeterminate ? 'mixed' : node.isSelected}
            onChange={(event) => onCheckNode(node, event.target.checked)}
            ref={(element) => {
              if (element) {
                element.indeterminate = node.isIndeterminate
              }
            }}
          />
          {node.label}
          <SelectionIndicator node={node} />
        </label>
      </div>

      {hasChildren && node.isOpen && (
        <ul role="group">
          {node.children?.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              onToggleNode={onToggleNode}
              onCheckNode={onCheckNode}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

export const CheckboxTree = ({treeData, onToggleNode, onCheckNode, className}: CheckboxTreeProps) => {
  return (
    <div
      className={
        className ??
        'absolute left-0 right-0 mt-[4px] bg-container_background rounded-lg shadow-lg z-10 p-2 max-h-[500px] overflow-auto border-1px border-border_highlight_10'
      }
    >
      <ul role="tree">
        {treeData.map((node) => (
          <TreeNodeRow
            key={node.id}
            node={node}
            depth={0}
            onToggleNode={onToggleNode}
            onCheckNode={onCheckNode}
          />
        ))}
      </ul>
    </div>
  )
}
